import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketService } from '../../../api/ticketService';
import inquiryCategoryMasterService from '../../../api/inquiryCategoryMasterService';
import { useStatuses } from '../../../hooks/useStatuses';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getCurrentUser } from '../../../api/authService';
import {
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveCurrentSlaWindow,
} from '../../../utils/inquiryDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { formatDateTime, formatTimeFromMinutes, minutesToHHMMSS2 } from '../../../components/globaldeclaration';
import { 
  Ticket, 
  Search, 
  Filter, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Download,
  ChevronDown,
  FileText,
  BarChart3,
  Eye
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const getRequesterDisplayName = (ticket) =>
  ticket?.requesterFullName ||
  ticket?.requesterName ||
  ticket?.requester?.fullName ||
  ticket?.createdByName ||
  'N/A';

/** Split formatted datetime onto two lines so columns don't overflow into Action. */
const renderDateTimeCell = (value, emptyLabel = '') => {
  const formatted = formatDateTime(value);
  if (!formatted || formatted === '-') return emptyLabel;
  const spaceIdx = formatted.indexOf(' ');
  if (spaceIdx < 0) {
    return <span className="leading-snug break-words">{formatted}</span>;
  }
  return (
    <div className="leading-snug break-words">
      <div>{formatted.slice(0, spaceIdx)}</div>
      <div className="text-muted-foreground">{formatted.slice(spaceIdx + 1)}</div>
    </div>
  );
};

const MyTicketsOverview = () => {
  const navigate = useNavigate();
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartDataLoading, setChartDataLoading] = useState(false);
  const [reportStats, setReportStats] = useState({ totalTickets: 0, byStatus: [], byPriority: [] });
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState('');
  const ticketsFetchIdRef = useRef(0);
  const statsFetchIdRef = useRef(0);
  
  // Get current logged-in user ID
  const currentUser = getCurrentUser();
  const loggedInUserId = currentUser?.userId || sessionStorage.getItem('userId');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  
  const { data: statuses = [], isLoading: loadingStatuses } = useStatuses();
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [loadingInquiryMasters, setLoadingInquiryMasters] = useState(false);

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInquiryMasters(true);
      try {
        const [priorityRows, categoryResp] = await Promise.all([
          ticketService.getInquiryPriorityLevelsForCreate(),
          inquiryCategoryMasterService.getAll(true),
        ]);
        if (cancelled) return;
        setInquiryPriorityLevels(Array.isArray(priorityRows) ? priorityRows : []);
        const catData = categoryResp?.data ?? categoryResp;
        const catRows = Array.isArray(catData) ? catData : (catData?.content || []);
        setInquiryCategories(Array.isArray(catRows) ? catRows : []);
      } catch (e) {
        console.warn('Failed to load inquiry masters for My Tickets Overview', e);
      } finally {
        if (!cancelled) setLoadingInquiryMasters(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build shared filter params (list adds page/size; stats uses filters only)
  const buildFilterParams = () => {
    const params = {
      assigneeId: parseInt(loggedInUserId)
    };

    if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
      params.search = debouncedSearchTerm.trim();
    }
    if (selectedStatus !== 'all') {
      const selectedStatusObj = statuses.find(s => {
        const statusValue = s.statusCode || s.code || String(s.statusId || s.id);
        return statusValue === selectedStatus;
      });
      const statusCode = selectedStatusObj?.statusCode || selectedStatusObj?.code || selectedStatus;
      params.status = statusCode;
    }
    if (selectedPriority !== 'all') {
      const priorityId = parseInt(selectedPriority, 10);
      if (!isNaN(priorityId)) params.inquiryPriorityLevelId = priorityId;
    }
    if (selectedCategory !== 'all') {
      const categoryId = parseInt(selectedCategory, 10);
      if (!isNaN(categoryId)) params.inquiryCategoryId = categoryId;
    }
    if (selectedSource !== 'all') params.source = selectedSource;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '' || Number.isNaN(params[key])) {
        delete params[key];
      }
    });
    return params;
  };

  // Build API params for list endpoint (reused by loadTickets and export)
  const buildListParams = (overrides = {}) => {
    const params = {
      ...buildFilterParams(),
      page: overrides.page !== undefined ? overrides.page : currentPage,
      size: overrides.size !== undefined ? overrides.size : pageSize
    };
    if (params.search) {
      params.searchQuery = params.search;
    }
    return params;
  };

  const loadReportStats = async () => {
    if (!loggedInUserId) return;
    const fetchId = ++statsFetchIdRef.current;
    try {
      setChartDataLoading(true);
      const response = await ticketService.getListTicketsReportStatistics(buildFilterParams());
      if (fetchId !== statsFetchIdRef.current) return;
      const data = response?.data?.data || response?.data || {};
      setReportStats({
        totalTickets: Number(data.totalTickets) || 0,
        byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
        byPriority: Array.isArray(data.byPriority) ? data.byPriority : []
      });
    } catch (err) {
      if (fetchId !== statsFetchIdRef.current) return;
      console.warn('My tickets overview statistics load failed:', err);
      setReportStats({ totalTickets: 0, byStatus: [], byPriority: [] });
    } finally {
      if (fetchId === statsFetchIdRef.current) {
        setChartDataLoading(false);
      }
    }
  };

  const loadTickets = async () => {
    if (!loggedInUserId) {
      console.warn('User ID not available');
      return;
    }

    const fetchId = ++ticketsFetchIdRef.current;
    try {
      setLoading(true);
      setError('');

      const params = buildListParams();

      // Call the API with all filters
      const response = await ticketService.getAllTickets(params);
      if (fetchId !== ticketsFetchIdRef.current) return;
      
      const responseData = response?.data;
      
      let ticketsList = [];
      let total = 0;
      let pages = 0;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        
        // Handle paginated response structure
        if (paginatedData.content) {
          ticketsList = paginatedData.content;
          total = paginatedData.totalElements || 0;
          pages = paginatedData.totalPages || 0;
        } else if (Array.isArray(paginatedData)) {
          ticketsList = paginatedData;
          total = paginatedData.length;
          pages = 1;
        }
      } else if (Array.isArray(responseData)) {
        ticketsList = responseData;
        total = responseData.length;
        pages = 1;
      }
      
      // Store fetched tickets (current page only)
      setFilteredTickets(ticketsList);
      
      // Store API pagination data
      setPaginationData({
        totalElements: total,
        totalPages: pages,
        pageNumber: currentPage,
        pageSize: pageSize,
        first: currentPage === 0,
        last: currentPage >= pages - 1
      });
    } catch (error) {
      if (fetchId !== ticketsFetchIdRef.current) return;
      console.error('Error loading tickets:', error);
      setError(extractErrorMessage(error, 'Failed to load tickets'));
      toast.error(extractErrorMessage(error, 'Failed to load tickets'));
      setFilteredTickets([]);
      setPaginationData(null);
    } finally {
      if (fetchId === ticketsFetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Paginated table — refetch on page + filter changes
  useEffect(() => {
    if (loggedInUserId) {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchTerm, selectedStatus, selectedPriority, selectedCategory, selectedSource, fromDate, toDate, loggedInUserId]);

  // Charts — full filtered dataset via statistics API (not current page)
  useEffect(() => {
    if (loggedInUserId) {
      loadReportStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedStatus, selectedPriority, selectedCategory, selectedSource, fromDate, toDate, loggedInUserId, statuses]);

  const refreshReport = () => {
    loadTickets();
    loadReportStats();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setSelectedPriority('all');
    setSelectedCategory('all');
    setSelectedSource('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
  };

  // Escape CSV cell (wrap in quotes, escape internal quotes)
  const escapeCsvCell = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val).trim();
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  /** Fetch full ticket list for export (same filters, size=totalElements) */
  const fetchAllTicketsForExport = async () => {
    const initialParams = buildListParams({ page: 0, size: 10 });
    const initialResponse = await ticketService.getAllTickets(initialParams);
    const initialData = initialResponse?.data;
    let totalElements = 0;
    if (initialData?.success && initialData?.data) {
      const paginated = initialData.data;
      totalElements = paginated.totalElements ?? (Array.isArray(paginated.content) ? paginated.content.length : 0);
    }
    if (totalElements === 0) return [];
    const fullParams = buildListParams({ page: 0, size: totalElements });
    const fullResponse = await ticketService.getAllTickets(fullParams);
    const fullData = fullResponse?.data;
    let content = [];
    if (fullData?.success && fullData?.data?.content) {
      content = fullData.data.content;
    } else if (Array.isArray(fullData?.data)) {
      content = fullData.data;
    }
    return content;
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    if (!loggedInUserId) {
      toast.error('User ID not available. Please sign in again.');
      return;
    }
    setExporting(true);
    try {
      const content = await fetchAllTicketsForExport();
      if (!content.length) {
        toast('No tickets to export.', { icon: 'ℹ️' });
        return;
      }
      const headers = ['Ticket ID', 'Ticket Title', 'Inquiry Type', 'Inquiry Category', 'Inquiry Sub Category', 'Inquiry Priority', 'Source', 'Status', 'Created Date', 'Due Date', 'SLA Status', 'Resolution Time'];
      const rows = content.map((ticket) => {
        const slaWindow = resolveCurrentSlaWindow(ticket, 0, statuses);
        const slaStatus = slaWindow?.label || getSlaStatus(ticket);
        const resolutionTime = ticket.slaElapsedMinutes != null ? minutesToHHMMSS2(ticket.slaElapsedMinutes) : '';
        const dueDateVal = ticket.dueDate || ticket.escalatedTime;
        return [
          escapeCsvCell(`#${ticket.ticketNumber || ticket.ticketId || ticket.id || ''}`),
          escapeCsvCell(ticket.subject || ticket.title || 'N/A'),
          escapeCsvCell(ticket.inquiryTypeName || '—'),
          escapeCsvCell(resolveTicketCategoryLabel(ticket)),
          escapeCsvCell(ticket.inquirySubCategoryName || '—'),
          escapeCsvCell(resolveTicketPriorityLabel(ticket)),
          escapeCsvCell(ticket.source || 'Portal'),
          escapeCsvCell(ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A'),
          escapeCsvCell(ticket.createdAt || ticket.createdDate ? formatDateTime(ticket.createdAt || ticket.createdDate) : ''),
          escapeCsvCell(dueDateVal ? formatDateTime(dueDateVal) : ''),
          escapeCsvCell(slaStatus),
          escapeCsvCell(resolutionTime)
        ];
      });
      const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-tickets-overview-${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${content.length} ticket(s) to CSV.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(extractErrorMessage(err, 'Failed to export tickets. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    if (!loggedInUserId) {
      toast.error('User ID not available. Please sign in again.');
      return;
    }
    setExporting(true);
    try {
      const content = await fetchAllTicketsForExport();
      if (!content.length) {
        toast('No tickets to export.', { icon: 'ℹ️' });
        return;
      }
      const doc = new jsPDF('l', 'mm', 'a4');
      const headers = [['Ticket ID', 'Title', 'Category', 'Priority', 'Source', 'Status', 'Created', 'Due', 'SLA', 'Resolution']];
      const body = content.map((ticket) => {
        const slaWindow = resolveCurrentSlaWindow(ticket, 0, statuses);
        const slaStatus = slaWindow?.label || getSlaStatus(ticket);
        const resolutionTime = ticket.slaElapsedMinutes != null ? minutesToHHMMSS2(ticket.slaElapsedMinutes) : '';
        const dueDateVal = ticket.dueDate || ticket.escalatedTime;
        return [
          `#${ticket.ticketNumber || ticket.ticketId || ticket.id || ''}`.substring(0, 14),
          (ticket.subject || ticket.title || 'N/A').substring(0, 22),
          resolveTicketCategoryLabel(ticket).substring(0, 12),
          resolveTicketPriorityLabel(ticket).substring(0, 10),
          (ticket.source || 'Portal').substring(0, 8),
          (ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A').substring(0, 10),
          ticket.createdAt || ticket.createdDate ? formatDateTime(ticket.createdAt || ticket.createdDate).substring(0, 16) : '',
          dueDateVal ? formatDateTime(dueDateVal).substring(0, 16) : '',
          String(slaStatus).substring(0, 14),
          resolutionTime.substring(0, 12)
        ];
      });
      const config = { head: headers, body, startY: 20, styles: { fontSize: 7 }, headStyles: { fillColor: [34, 197, 94] } };
      if (typeof autoTable === 'function') autoTable(doc, config);
      else if (typeof doc.autoTable === 'function') doc.autoTable(config);
      else throw new Error('PDF table plugin not available.');
      doc.save(`my-tickets-overview-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Exported ${content.length} ticket(s) to PDF.`);
    } catch (err) {
      console.error('Export PDF failed:', err);
      toast.error(extractErrorMessage(err, 'Failed to export tickets to PDF. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getPriorityBadgeColor = (priority) => {
    const priorityLower = (priority || '').toLowerCase();
    if (priorityLower.includes('critical')) return 'bg-red-100 text-red-800 border-red-300';
    if (priorityLower.includes('high')) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (priorityLower.includes('medium')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (priorityLower.includes('low')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusBadgeColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower === 'in progress' || statusLower === 'in-progress') return 'bg-purple-100 text-purple-800 border-purple-300';
    if (statusLower === 'resolved') return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower === 'closed') return 'bg-gray-100 text-gray-800 border-gray-300';
    if (statusLower === 'escalated') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Get SLA status from ticket — prefer current window over historical slaIsBreached
  const getSlaStatus = (ticket) => {
    const window = resolveCurrentSlaWindow(ticket, 0, statuses);
    if (window) {
      return window.isOverdue ? 'overdue' : 'on-track';
    }

    const status = (ticket.status?.statusCode || ticket.statusName || ticket.status || '').toLowerCase();
    if (status === 'closed' || status === 'resolved') {
      if (ticket.slaIsBreached === true || ticket.slaBreach === true) return 'breach';
      return 'met';
    }

    if (ticket.slaStatus) {
      return String(ticket.slaStatus).toLowerCase();
    }

    return 'on-track';
  };

  // Get SLA status color
  const getSlaStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track':
      case 'met':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'breach':
      case 'overdue':
      case 'breached':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const hasActiveFilters = searchTerm || selectedStatus !== 'all' || selectedPriority !== 'all' || selectedCategory !== 'all' || selectedSource !== 'all' || fromDate || toDate;

  const pieChartData = useMemo(() => {
    return (reportStats.byStatus || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        value: Number(item.count) || 0
      }))
      .filter((item) => item.value > 0);
  }, [reportStats]);

  const handleViewTicket = (ticket) => {
    const ticketId = ticket?.ticketId || ticket?.id;
    if (!ticketId) {
      toast.error('Ticket ID not available');
      return;
    }
    navigate(`/agent/tickets/${ticketId}`, { state: { mode: 'view' } });
  };

  const barChartData = useMemo(() => {
    return (reportStats.byPriority || [])
      .map((item) => {
        const name = String(item.name || 'Unknown').trim() || 'Unknown';
        return {
          name: name.substring(0, 12),
          count: Number(item.count) || 0,
          fullName: name
        };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [reportStats]);

  const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Tickets Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and filter all your assigned tickets with detailed information
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowFilters((prev) => !prev);
              setExportMenuOpen(false);
            }}
            className="flex items-center gap-2"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasActiveFilters && !showFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" aria-hidden />
            )}
          </Button>
          <div className="relative">
            <Button
              onClick={() => setExportMenuOpen((prev) => !prev)}
              disabled={loading || exporting || (paginationData && paginationData.totalElements === 0)}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4 opacity-70" />
            </Button>
            {exportMenuOpen && !exporting && (
              <>
                <div className="fixed inset-0 z-10" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 min-w-[160px] rounded-md border border-border bg-card py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button
            onClick={refreshReport}
            disabled={loading || chartDataLoading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading || chartDataLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters Card (toggle via Filter button) */}
      {showFilters && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* All Filters in Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Filter - Reduced size */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by ticket ID, title..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* From Date Filter - Moved after search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                From Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={fromDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const today = new Date().toISOString().split('T')[0];
                    if (selectedDate > today) {
                      toast.error('From Date cannot be in the future');
                      return;
                    }
                    if (toDate && selectedDate > toDate) {
                      toast.error('From Date cannot be after To Date');
                      return;
                    }
                    setFromDate(selectedDate);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* To Date Filter - Moved after search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                To Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const today = new Date().toISOString().split('T')[0];
                    if (selectedDate > today) {
                      toast.error('To Date cannot be in the future');
                      return;
                    }
                    if (fromDate && selectedDate < fromDate) {
                      toast.error('To Date cannot be before From Date');
                      return;
                    }
                    setToDate(selectedDate);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={selectedStatus}
                onValueChange={(value) => {
                  setSelectedStatus(value);
                  setCurrentPage(0);
                }}
                disabled={loadingStatuses}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((status) => {
                    let statusValue = status.statusCode || status.code;
                    
                    if (!statusValue && status.statusName) {
                      statusValue = status.statusName.toLowerCase().replace(/\s+/g, '-');
                    }
                    
                    if (!statusValue) {
                      statusValue = String(status.statusId || status.id);
                    }
                    
                    const statusDisplay = status.statusName || status.name || 'Unknown';
                    return (
                      <SelectItem
                        key={status.statusId || status.id}
                        value={statusValue}
                      >
                        {statusDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Priority
              </label>
              <Select
                value={selectedPriority}
                onValueChange={(value) => {
                  setSelectedPriority(value);
                  setCurrentPage(0);
                }}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {inquiryPriorityLevels.map((priority) => {
                    const priorityId = priority.id || priority.inquiryPriorityLevelId;
                    const priorityValue = priorityId ? String(priorityId) : 'all';
                    const priorityDisplay = priority.priorityLevelName || priority.name || 'Unknown';
                    
                    return (
                      <SelectItem
                        key={priorityId || `priority-${priorityDisplay}`}
                        value={priorityValue}
                      >
                        {priorityDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Category
              </label>
              <Select
                value={selectedCategory}
                onValueChange={(value) => {
                  setSelectedCategory(value);
                  setCurrentPage(0);
                }}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {inquiryCategories.map((category) => {
                    const id = category.id || category.inquiryCategoryId;
                    return (
                      <SelectItem
                        key={id}
                        value={String(id)}
                      >
                        {category.inquiryCategoryName || category.name || 'Unknown'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Source
              </label>
              <Select
                value={selectedSource}
                onValueChange={(value) => {
                  setSelectedSource(value);
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">whatsapp</SelectItem>
                  <SelectItem value="chatbot">Chatbot</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="lg:col-span-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Charts: Pie (by status) and Bar (by priority) — from statistics API (all matching tickets) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by status
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Distribution of all assigned tickets by status
              </p>
            </CardHeader>
            <CardContent>
              {chartDataLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieChartData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Ticket className="h-5 w-5" />
                Tickets by priority
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Count of all assigned tickets per priority
              </p>
            </CardHeader>
            <CardContent>
              {chartDataLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" allowDecimals={false} className="text-xs" />
                    <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [value, 'Tickets']}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
      </div>

      {/* Tickets Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Tickets
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading tickets...</span>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tickets found</p>
            </div>
          ) : (
            <>
              <div className="w-full min-w-0 overflow-x-auto">
                <table className="w-full table-fixed border-collapse min-w-[1680px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[3%]">S.No</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">Ticket ID</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[9%]">Requester Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[11%]">Ticket Title</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Category</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[7%]">Priority</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Source</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[11%] min-w-[11rem]">Status</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[9%]">Created Date</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[9%]">Due Date</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">SLA Status</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[7%]">Resolution Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket, index) => {
                      const requesterName = getRequesterDisplayName(ticket);
                      return (
                      <tr
                        key={ticket.ticketId || ticket.id}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          {currentPage * pageSize + index + 1}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top whitespace-nowrap overflow-hidden">
                          #{ticket.ticketNumber || ticket.ticketId || ticket.id}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          <span className="truncate block" title={requesterName}>
                            {requesterName}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          <p
                            className="font-medium line-clamp-3 leading-snug break-words"
                            title={ticket.subject || ticket.title || 'N/A'}
                          >
                            {ticket.subject || ticket.title || 'N/A'}
                          </p>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          {resolveTicketCategoryLabel(ticket)}
                        </td>
                        <td className="p-3 align-top overflow-hidden">
                          <Badge
                            className={`${getPriorityBadgeColor(
                              resolveTicketPriorityLabel(ticket)
                            )} border whitespace-nowrap`}
                          >
                            {resolveTicketPriorityLabel(ticket)}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-foreground capitalize align-top whitespace-nowrap overflow-hidden">
                          {ticket.source || 'Portal'}
                        </td>
                        <td className="p-3 align-top overflow-hidden">
                          <Badge
                            className={`${getStatusBadgeColor(
                              ticket.status?.statusName || ticket.statusName || ticket.status
                            )} border max-w-full whitespace-normal text-center leading-tight`}
                          >
                            {ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A'}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          {renderDateTimeCell(ticket.createdAt || ticket.createdDate)}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          {renderDateTimeCell(ticket.escalatedTime || ticket.dueDate)}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          <div className="flex flex-col gap-1">
                            {(() => {
                              const slaWindow = resolveCurrentSlaWindow(ticket, 0, statuses);
                              const slaStatus = slaWindow
                                ? (slaWindow.isOverdue ? 'overdue' : 'on-track')
                                : getSlaStatus(ticket);
                              const label = slaWindow?.label || slaStatus;
                              return (
                                <>
                                  <span className={`text-xs font-medium ${getSlaStatusColor(slaStatus)}`}>
                                    {label}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top overflow-hidden">
                          <div className="flex flex-col gap-1">
                            {(() => {
                              const slaStatus = getSlaStatus(ticket);
                              return (
                                <>
                                 
                                  <span className="text-xs text-muted-foreground">
                                    {minutesToHHMMSS2(ticket.slaElapsedMinutes)}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="p-3 text-sm align-top whitespace-nowrap overflow-hidden">
                          <Button
                            size="sm"
                            variant="outline"
                            className="inline-flex items-center gap-1.5 shrink-0"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <Eye className="h-4 w-4 shrink-0" />
                            View
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} tickets
                  </div>
                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}
                    
                    {/* Previous Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}
                    
                    {/* Page Numbers */}
                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;
                          
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );
                            
                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);
                            
                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }
                            
                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    )}
                    
                    {/* Next Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Last Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyTicketsOverview;
